import { useMemo, useState, useEffect } from "react";
// outsource
import { Layout, Col, Row } from "antd";
// redux
import { useDispatch, useSelector } from "react-redux";
import { snapPayoutDaily, unSnapPayoutDaily } from "../../store/payout_daily/payout_daily.action";
import { payoutDailySelector } from "../../store/payout_daily/payout_daily.selector";
import { snapBranchs, unSnapBranchs } from "../../store/branch/branch.action";
import { branchsSelector } from "../../store/branch/branch.selector";
// components
import PayoutReportToolbar from "./component/payout_report_toolbar";
import PayoutReportTable from "./component/payout_report_table";
import PayoutReportChart from "./component/payout_report_chart";
import LayoutBreadCrumb from "../00_Nav/layout/layout_bread_crumb";
import LayoutFooter from "../00_Nav/layout/layout_footer";
// functions
import { formatMoney } from "./functions/payout_report_format";
import {
  getStartOfWeek,
  getStartOfMonth,
  getEndOfMonth,
  addDays,
  addMonths,
  toDateNum,
} from "./functions/payout_report_date";
// style
import "./style.css";
import { URL } from "../00_Nav/url";

const { Content } = Layout;

const CHART_COLORS = [
  "#20B2AA", "#3F46C9", "#FF850F", "#DE3A87",
  "#7B83F2", "#66DA5C", "#FFD7B7", "#FFE58A",
  "#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4",
];

const PayoutReportScreen = () => {
  // -------------------------- VAR --------------------------
  const breadCrumb = [
    { title: "Home", url: URL.home },
    { title: "Report", url: URL.report },
    { title: "Payout Report", url: URL.payoutReport },
  ];

  // -------------------------- REDUX --------------------------
  const dispatch = useDispatch();
  const dailyRecords = useSelector(payoutDailySelector);
  const branches = useSelector(branchsSelector);

  // -------------------------- STATE --------------------------
  const [reportBy, setReportBy] = useState("week");
  const [currentWeekStart, setCurrentWeekStart] = useState(getStartOfWeek(new Date()));
  const [currentMonthStart, setCurrentMonthStart] = useState(getStartOfMonth(new Date()));

  // -------------------------- EFFECT --------------------------
  useEffect(() => {
    dispatch(snapBranchs());
    return () => dispatch(unSnapBranchs());
  }, [dispatch]);

  useEffect(() => {
    const start = reportBy === "month" ? currentMonthStart : currentWeekStart;
    const end = reportBy === "month" ? getEndOfMonth(currentMonthStart) : addDays(currentWeekStart, 6);
    const startDate = toDateNum(start);
    const endDate = toDateNum(end);
    dispatch(snapPayoutDaily({ startDate, endDate }));
    return () => dispatch(unSnapPayoutDaily());
  }, [dispatch, reportBy, currentWeekStart, currentMonthStart]);

  // -------------------------- FUNCTION --------------------------
  const handleReportByChange = (value) => {
    setReportBy(value);
  };

  const handlePreviousPeriod = () => {
    if (reportBy === "month") {
      setCurrentMonthStart((prev) => addMonths(prev, -1));
      return;
    }
    setCurrentWeekStart((prev) => addDays(prev, -7));
  };

  const nextDisabled = useMemo(() => {
    const currentStart = reportBy === "month"
      ? getStartOfMonth(new Date())
      : getStartOfWeek(new Date());
    const selectedStart = reportBy === "month" ? currentMonthStart : currentWeekStart;
    return selectedStart.getTime() >= currentStart.getTime();
  }, [reportBy, currentWeekStart, currentMonthStart]);

  const handleNextPeriod = () => {
    if (nextDisabled) return;
    if (reportBy === "month") {
      setCurrentMonthStart((prev) => {
        const next = addMonths(prev, 1);
        const currentStart = getStartOfMonth(new Date());
        return next.getTime() > currentStart.getTime() ? currentStart : next;
      });
      return;
    }
    setCurrentWeekStart((prev) => {
      const next = addDays(prev, 7);
      const currentStart = getStartOfWeek(new Date());
      return next.getTime() > currentStart.getTime() ? currentStart : next;
    });
  };

  const handlePeriodChange = (date) => {
    if (!date) return;
    if (reportBy === "month") {
      setCurrentMonthStart(getStartOfMonth(date.toDate()));
      return;
    }
    setCurrentWeekStart(getStartOfWeek(date.toDate()));
  };

  const reportData = useMemo(() => {
    const map = {};
    (dailyRecords || []).forEach((rec) => {
      const bid = rec.branchId;
      if (!bid) return;
      if (!map[bid]) map[bid] = { branchId: bid, totalPayout: 0 };
      map[bid].totalPayout += rec.totalPayout || 0;
    });

    const round2 = (n) => Math.round(n * 100) / 100;
    const grandTotal = Object.values(map).reduce((s, x) => s + x.totalPayout, 0);

    return Object.values(map).map((item, idx) => {
      const branch = (branches || []).find((b) => b.id === item.branchId);
      const shortName = branch?.name || item.branchId;
      const shortCode = branch?.shopId || item.branchId;
      return {
        id: item.branchId,
        name: branch ? `${shortName} | ${shortCode}` : item.branchId,
        shortName,
        shortCode,
        code: shortCode,
        totalPayout: round2(item.totalPayout),
        reportPercent: grandTotal ? (item.totalPayout / grandTotal) * 100 : 0,
        color: CHART_COLORS[idx % CHART_COLORS.length],
      };
    });
  }, [dailyRecords, branches]);

  const totalPayout = useMemo(
    () => reportData.reduce((sum, item) => sum + item.totalPayout, 0),
    [reportData],
  );

  // -------------------------- RENDER --------------------------
  const renderBody = () => {
    return (
      <Row>
        <Col xl={24} lg={24} md={24} xs={24}>
          <div className="payout-report-page">
            <PayoutReportToolbar
              reportBy={reportBy}
              currentMonthStart={currentMonthStart}
              currentWeekStart={currentWeekStart}
              handleReportByChange={handleReportByChange}
              handlePeriodChange={handlePeriodChange}
              handlePreviousPeriod={handlePreviousPeriod}
              handleNextPeriod={handleNextPeriod}
              nextDisabled={nextDisabled}
            />
            {reportData.length > 0 && (
              <Row gutter={[24, 24]} align="middle">
                <Col xl={14} lg={14} md={24} xs={24}>
                  <div className="payout-report-total">
                    Total Payout: <b>${formatMoney(totalPayout)}</b>
                  </div>
                  <PayoutReportTable reportData={reportData} formatMoney={formatMoney} />
                </Col>
                <Col xl={10} lg={10} md={24} xs={24}>
                  <PayoutReportChart reportData={reportData} formatMoney={formatMoney} />
                </Col>
              </Row>
            )}
          </div>
        </Col>
      </Row>
    );
  };

  // -------------------------- MAIN --------------------------
  return (
    <Layout className="site-layout full">
      <Content className="content">
        <div style={{ paddingLeft: 16 }}>
          <LayoutBreadCrumb data={breadCrumb} />
        </div>
        {renderBody()}
      </Content>
      <LayoutFooter />
    </Layout>
  );
};

export default PayoutReportScreen;
